import React, { useState } from 'react';

const ProductModal = ({ product, isOpen, onClose }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  if (!isOpen || !product) return null;

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-secondary">Product Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-300"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div>
              {/* Main Image */}
              <div className="mb-4">
                <img
                  src={product.image || '/clock-with-lamp-it-that-says-word-it.jpg'}
                  alt={product.partsName}
                  className="w-full h-80 object-cover rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
                  onClick={() => handleImageClick(product.image || '/clock-with-lamp-it-that-says-word-it.jpg')}
                />
              </div>
              
              {/* Additional Images - Only show if there are more than 1 image total */}
              {product.additionalImages && product.additionalImages.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-secondary mb-3">Additional Images</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {product.additionalImages.map((image, index) => (
                      <div key={index} className="relative group cursor-pointer" onClick={() => handleImageClick(image)}>
                        <img
                          src={image}
                          alt={`${product.partsName} - Additional view ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-300"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-secondary mb-2">
                  {product.partsName}
                </h3>
                <p className="text-lg text-gray-600">
                  {product.description || 'No description available'}
                </p>
              </div>

              {/* Specifications */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h4 className="text-xl font-semibold text-secondary mb-4">Specifications</h4>
                
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-700">Part Number:</span>
                    <span className="text-primary font-semibold">{product.partsNumber}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-700">Brand:</span>
                    <span className="text-gray-900">{product.brand?.name || product.brand}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-700">Model:</span>
                    <span className="text-gray-900">{product.model}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-700">Equipment Type:</span>
                    <span className="text-gray-900">{product.typeOfEquipment?.name || product.typeOfEquipment}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-700">Manufacturer:</span>
                    <span className="text-gray-900">{product.manufacturer?.name || product.manufacturer}</span>
                  </div>
                  
                  {product.partCategory && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="font-medium text-gray-700">Part Category:</span>
                      <span className="text-primary font-semibold">{product.partCategory?.name || product.partCategory}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-secondary">Description</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    {product.description || 'No description available for this product.'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4">
                <button
                  onClick={() => {
                    onClose();
                    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary w-full"
                >
                  Contact for Information
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-7xl max-h-full">
            <img
              src={selectedImage}
              alt="Zoomed view"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeImageModal();
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-300 bg-black bg-opacity-50 rounded-full p-2"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductModal;